import React, { useState, useEffect } from "react";
import {
  Steps,
  Button,
  Input,
  Select,
  Upload,
  message,
  Form,
  Image,
  Spin,
  Typography,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  HomeOutlined,
  FilePdfOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import AdminDashboard from "../Layout/AdminDashboard";
import UpdateFssaiForm from "./UpdateFssaiForm";
import { useAuth } from "../Context/AuthContext";
import { useForm } from "antd/es/form/Form";
import { useForm as useRHForm, Controller } from "react-hook-form";


const { Step } = Steps;
const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const AuditReport = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [auditItems, setAuditItems] = useState([]); // always keep an array
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileLists, setFileLists] = useState({});
  const [preloadedFiles, setPreloadedFiles] = useState({});
  const [removedFiles, setRemovedFiles] = useState({});
  const [isEditing, setIsEditing] = useState({});
  const [form] = Form.useForm();

  const [auditService, setAuditService] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsEditing, setSuggestionsEditing] = useState(false);
  const [suggestionsForm] = Form.useForm();

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const checklistId = searchParams.get("checklistId");
  const category = searchParams.get("category");

  // File validation
  const validateFile = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      message.error("Only JPG, JPEG, PNG, and PDF files are allowed.");
      return false;
    }
    if (file.size > maxSize) {
      message.error("File size must be less than 5MB.");
      return false;
    }
    return true;
  };

  // Fetch sections data
  const fetchSections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/api/auditor/fetchingQuestionAnswer/${params.audit_id}?checkListId=${checklistId}`
      );
      const data = response?.data?.data || [];
      const fetchedSuggestions = response?.data?.suggestions || [];
      setSections(data);
      setSuggestions(Array.isArray(fetchedSuggestions) ? fetchedSuggestions : []);

      // populate suggestions form if suggestions exist
      if (Array.isArray(fetchedSuggestions) && fetchedSuggestions.length) {
        suggestionsForm.setFieldsValue({
          suggestions: fetchedSuggestions.map((s) => ({ value: s })),
        });
      } else {
        suggestionsForm.setFieldsValue({ suggestions: [] });
      }

      // Preload files
      const files = {};
      data.forEach((section) => {
        section.questions?.forEach((question) => {
          if (question.image_url) files[question.questionId] = question.image_url;
        });
      });
      setPreloadedFiles(files);
      setRemovedFiles({});
      setFileLists({});
    } catch (error) {
      console.error("fetchSections error:", error);
      message.error("Failed to fetch sections.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch audit items for steps (robust to different response shapes)
  const fetchAuditItems = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(
        `${apiUrl}/api/auditor/fetchLabelsWithQuestions/${checklistId}`
      );

      setAuditService(resp.data.service);
      let data = resp?.data;

      // Normalize to array
      let items = [];
      if (Array.isArray(data)) {
        items = data;
      } else if (data?.data && Array.isArray(data.data)) {
        items = data.data;
      } else if (data?.labels && Array.isArray(data.labels)) {
        items = data.labels;
      } else if (typeof data === "object" && data !== null) {
        items = Object.values(data);
      } else {
        items = [];
      }

      if (!Array.isArray(items)) {
        console.warn("fetchAuditItems: normalized items is not an array:", items);
        items = [];
      }

      setAuditItems(items);

    } catch (error) {
      console.error("fetchAuditItems error:", error);
      message.error("Failed to fetch audit items.");
      setAuditItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (checklistId) fetchAuditItems();
    if (params.audit_id && checklistId) fetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistId, params.audit_id]);

  const toggleEditing = (sectionIndex) => {
    setIsEditing((prev) => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex],
    }));
  };

  const handleChange = (questionId, info) => {
    const file = info.file.originFileObj;
    if (!file) return;

    if (!validateFile(file)) {
      setFileLists((prev) => {
        const copy = { ...prev };
        delete copy[questionId];
        return copy;
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFileLists((prev) => ({
        ...prev,
        [questionId]: { file, preview: reader.result },
      }));
      setRemovedFiles((prev) => {
        if (!prev[questionId]) return prev;
        const copy = { ...prev };
        delete copy[questionId];
        return copy;
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = (questionId) => {
    // Remove new uploaded file
    if (fileLists[questionId]) {
      setFileLists((prev) => {
        const copy = { ...prev };
        delete copy[questionId];
        return copy;
      });
    }
    // Mark preloaded file as removed
    if (preloadedFiles[questionId]) {
      setRemovedFiles((prev) => ({ ...prev, [questionId]: true }));
    }
  };

  const handleBeforeUpload = (file) => {
    return validateFile(file) || Upload.LIST_IGNORE;
  };

  const handleUpdateSection = async (sectionIndex) => {
    const sectionResponses = form.getFieldValue("responses") || {};
    const formData = new FormData();
    const deleted = [];

    const updatedResponses = sections[sectionIndex].questions.map((q) => {
      const qId = q.questionId;
      const resp = sectionResponses[qId] || {};
      const newFile = fileLists[qId]?.file;
      const wasRemoved = removedFiles[qId];

      let fileName = null;

      if (newFile) {
        formData.append("files", newFile, newFile.name);
        fileName = newFile.name;
      } else if (wasRemoved) {
        deleted.push(qId);
        fileName = null;
      } else if (preloadedFiles[qId]) {
        fileName = preloadedFiles[qId].split("/").pop();
      }

      return {
        questionId: qId,
        comment: resp.comment || "",
        selectedMark: resp.selectedMark ?? null,
        file: fileName,
      };
    });

    // Attach the normal update payload
    formData.append(
      "data",
      JSON.stringify({
        auditId: params.audit_id,
        responses: updatedResponses,
        deletedFiles: deleted,
      })
    );


    try {
      setLoading(true);
      const token = user?.token || localStorage.getItem("token");
      // console.log("Submitting section update with responses:", formData);
      await axios.post(`${apiUrl}/api/auditor/updateAuditResponses`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      message.success("Section updated successfully!");
      toggleEditing(sectionIndex);

      // If suggestions exist and are edited, also submit them alongside by calling the same endpoint
      if (suggestionsEditing) {
        console.log("Also submitting updated suggestions as part of section update.");
        const vals = suggestionsForm.getFieldValue("suggestions") || [];
        const arr = vals.map((s) => s.value).filter(Boolean);
        if (arr.length) {
          await axios.post(
            `${apiUrl}/api/auditor/updateAuditResponses`,
            { auditId: params.audit_id, suggestions: arr },
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
          );
        }
        setSuggestionsEditing(false);
      }

      await fetchSections();
    } catch (error) {
      console.error(error);
      message.error("Failed to update section.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSuggestions = async (values) => {
    // values.suggestions -> [{value: '...'}, ...]
    const arr = (values.suggestions || []).map((s) => s.value).filter(Boolean);

    try {
      setLoading(true);
      const token = user?.token || localStorage.getItem("token");
      // console.log("Submitting suggestions:", arr);
      // Use the same endpoint as section updates, but with suggestions payload

      const formData = new FormData();
      formData.append(
        "data",
        JSON.stringify({
          auditId: params.audit_id,
          suggestions: arr,
          responses: [], // Empty responses array
          deletedFiles: []
        })
      );
      await axios.post(
        `${apiUrl}/api/auditor/updateAuditResponses`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      message.success("Suggestions saved successfully!");
      setSuggestions(arr);
      setSuggestionsEditing(false);
      await fetchSections();
    } catch (err) {
      console.error("save suggestions error", err);
      message.error("Failed to save suggestions.");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => navigate(-1);
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const firstSegment = pathSegments[0];
  // const role = user?.role;

  const isSubmittedForApproval = firstSegment === "submittedForApproval";
  const isModified = firstSegment === "modified";
  const isSuperOrAuditAdmin =
    user?.role === "SUPER_ADMIN" ||
    user?.role === "AUDIT_ADMIN";

  const isAuditor = user?.role === "AUDITOR";

  // show  changes section 

  const canShowChangesSection = (!isAuditor && isSubmittedForApproval) || (isAuditor && isModified) || (isSuperOrAuditAdmin && isModified);
  // !isAuditor || 


  // editable

  const canEditChangesSection = isSuperOrAuditAdmin && isSubmittedForApproval;
  // console.log(firstSegment, isSuperOrAuditAdmin, isSubmittedForApproval, "firstsegment update auditor report ");

  const totalSteps = auditItems.length; // excluding FSSAI
  const isFinalStep = currentStep === totalSteps;


  const showAddChangesButton =
    canEditChangesSection && !isFinalStep;


  const showFinalSubmitButton =
    canEditChangesSection && isFinalStep;


  const [existingChanges, setExistingChanges] = useState({});

  const [changesForm] = Form.useForm();
  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useRHForm({
    defaultValues: {
      changes: {},
    },
  });

  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/api/auditor/getAuditById/${params.audit_id}`
        );

        const changes = res.data?.data?.changes || {};

        const normalized = {};
        Object.keys(changes).forEach((k) => {
          normalized[String(k)] = changes[k];
        });

        setExistingChanges(normalized);

        // ✅ set values into RHF
        Object.keys(normalized).forEach((key) => {
          setValue(`changes.${key}`, normalized[key]);
        });

      } catch (err) {
        console.error("Failed to load audit changes", err);
      }
    };

    if (params.audit_id) fetchAudit();
  }, [params.audit_id, setValue]);


  const handleSaveStepChanges = async (sectionIndex) => {
    const allChanges = getValues("changes") || {};
    const stepChange = allChanges[sectionIndex];

    if (!stepChange) {
      message.error("Please add changes before saving");
      return;
    }
    // console.log(stepChange, " step changes")
    try {
      setLoading(true);

      await axios.put(
        `${apiUrl}/api/auditor/updateAuditById/${params.audit_id}`,
        {
          //  NO status here
          changes: {
            [sectionIndex]: stepChange,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      setExistingChanges((prev) => ({
        ...prev,
        [sectionIndex]: stepChange,
      }));

      message.success(`Step ${sectionIndex + 1} changes saved`);
    } catch (err) {
      message.error("Failed to save changes");
    } finally {
      setLoading(false);
    }
  };

  // console.log(user)


  const handleFinalSubmit = async () => {
    const allChanges = getValues("changes") || {};
    const comment = `${user?.userName} given changes `;
    const userId = user?._id;

    if (!Object.keys(allChanges).length) {
      message.error("Please add changes before final submission");
      return;
    }
    // console.log(allChanges, "allchanges");

    try {
      setLoading(true);

      await axios.put(
        `${apiUrl}/api/auditor/updateAuditById/${params.audit_id}`,
        {
          status: "modified", // or submittedForApproval
          changes: allChanges, // 🔥 ALL steps
          comment,
          userId
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      message.success("Audit submitted successfully");
      navigate("/modified");
    } catch (err) {
      message.error("Final submission failed");
    } finally {
      setLoading(false);
    }
  };



  const renderQuestionsUI = () =>
    sections.map((section, sectionIndex) => (
      <div
        key={sectionIndex}
        style={{ display: sectionIndex === currentStep - 1 ? "block" : "none" }}
      >
        <div className="flex justify-between items-center my-4">
          <h2 className="text-lg font-bold">
            {section.title || `Section ${sectionIndex + 1}`}
          </h2>
          <Button
            type="primary"
            onClick={() => toggleEditing(sectionIndex)}
            disabled={!["draft", "modified"].includes(firstSegment)}
          >
            {isEditing[sectionIndex] ? "Cancel Edit" : "Edit Section"}
          </Button>
        </div>

        <Form
          layout="vertical"
          form={form}
          initialValues={{
            responses:
              section.questions?.reduce((acc, question) => {
                acc[question.questionId] = {
                  comment: question.comment || "",
                  selectedMark: question.marks ?? undefined,
                };
                return acc;
              }, {}) || {},
          }}
        >
          {section.questions
            ?.sort((a, b) => a.position - b.position)
            .map((question) => {
              const qId = question.questionId;
              const hasPreloaded = !!preloadedFiles[qId] && !removedFiles[qId];
              const hasNewFile = !!fileLists[qId]?.file;
              const isPdf = hasNewFile
                ? fileLists[qId].file.type === "application/pdf"
                : hasPreloaded && preloadedFiles[qId]?.toLowerCase().endsWith(".pdf");

              return (
                <div key={qId} className="mt-8 p-6 rounded-lg shadow-lg bg-white">
                  <div className="ml-2 border text-center rounded-lg p-2 w-[10%]">
                    <label className="text-md text-gray-800">Mark: {question.mark}</label>
                  </div>
                  <p className="text-gray-800 text-lg p-2">
                    {question.position} {question.description}
                  </p>

                  <div className="mt-4 flex items-center justify-around">
                    <Form.Item name={["responses", qId, "comment"]} className="mt-2 w-1/2">
                      <TextArea rows={3} placeholder="Comments..." disabled={!isEditing[sectionIndex]} />
                    </Form.Item>

                    <Form.Item name={["responses", qId, "selectedMark"]}>
                      <Select placeholder="Select a mark" style={{ width: 120 }} disabled={!isEditing[sectionIndex]}>
                        <Option key="N/A" value="N/A">N/A</Option>
                        {question.mark === 2
                          ? [0, 1, 2].map((val) => <Option key={val} value={val}>{val}</Option>)
                          : question.mark === 4
                            ? [0, 4].map((val) => <Option key={val} value={val}>{val}</Option>)
                            : null}
                      </Select>
                    </Form.Item>

                    <div style={{ marginBottom: "24px" }}>
                      {/* Preloaded file preview */}
                      {!isEditing[sectionIndex] && hasPreloaded && !hasNewFile && (
                        <div className="flex flex-col items-center">
                          {isPdf ? (
                            <a href={preloadedFiles[qId]} target="_blank" rel="noopener noreferrer">
                              <FilePdfOutlined style={{ fontSize: 48 }} />
                              <div>View PDF</div>
                            </a>
                          ) : (
                            <Image src={preloadedFiles[qId]} alt="preview" width={100} height={100} preview />
                          )}
                        </div>
                      )}

                      {/* Editing preloaded file */}
                      {isEditing[sectionIndex] && hasPreloaded && !hasNewFile && (
                        <div className="flex flex-col items-center">
                          {isPdf ? (
                            <a href={preloadedFiles[qId]} target="_blank" rel="noopener noreferrer">
                              <FilePdfOutlined style={{ fontSize: 48 }} />
                              <div>View PDF</div>
                            </a>
                          ) : (
                            <Image src={preloadedFiles[qId]} alt="preview" width={100} height={100} preview />
                          )}
                          <Button danger className="mt-2" icon={<DeleteOutlined />} onClick={() => handleRemoveFile(qId)}>
                            Remove
                          </Button>
                        </div>
                      )}

                      {/* Newly uploaded file */}
                      {hasNewFile && (
                        <div className="flex flex-col items-center">
                          {isPdf ? (
                            <a href={fileLists[qId].preview} target="_blank" rel="noopener noreferrer">
                              <FilePdfOutlined style={{ fontSize: 48 }} />
                              <div>View PDF</div>
                            </a>
                          ) : (
                            <Image src={fileLists[qId].preview} alt="Preview" width={100} height={100} preview />
                          )}
                          {isEditing[sectionIndex] && (
                            <Button danger className="mt-2" icon={<DeleteOutlined />} onClick={() => handleRemoveFile(qId)}>
                              Remove
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Upload button */}
                      {isEditing[sectionIndex] && !hasPreloaded && !hasNewFile && (
                        <Upload beforeUpload={handleBeforeUpload} onChange={(info) => handleChange(qId, info)} showUploadList={false} accept=".jpg,.jpeg,.png,.pdf">
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

          {isEditing[sectionIndex] && (
            <div className="flex justify-end mt-4">
              <Button type="primary" onClick={() => handleUpdateSection(sectionIndex)} loading={loading}>
                Update Section
              </Button>
            </div>
          )}
        </Form>
        {/* changes section */}

        {canShowChangesSection && (
          <div className="mt-8 p-6 rounded-lg shadow-lg bg-white">
            <div className="border rounded-lg px-4 py-2 mb-3">
              <span className="font-medium text-gray-800">
                Changes (Step {sectionIndex + 1})
              </span>
            </div>

            <Controller
              name={`changes.${sectionIndex}`}
              control={control}
              rules={
                canEditChangesSection && !isFinalStep
                  ? { required: "Please enter changes" }
                  : {}
              }
              render={({ field }) => (
                <TextArea
                  {...field}
                  rows={6}
                  placeholder="Enter changes for this step..."
                  disabled={!canEditChangesSection}
                />
              )}
            />

            {/* STEP SAVE BUTTON */}
            {/* {showAddChangesButton && (
              <div className="flex justify-end mt-4">
                <Button
                  type="primary"
                  loading={loading}
                  onClick={() => handleSaveStepChanges(sectionIndex)}
                >
                  Save Changes
                </Button>
              </div>
            )} */}

            {/* FINAL SUBMIT BUTTON */}
            {showFinalSubmitButton && (
              <div className="flex justify-end mt-4">
                <Button
                  type="primary"
                  danger
                  loading={loading}
                  onClick={handleFinalSubmit}
                >
                  Changes Submit
                </Button>
              </div>
            )}
          </div>
        )}


      </div>
    ));


  return (
    <AdminDashboard>
      <div className="p-8">
        <div className="flex justify-center mb-5">
          <h1 className="text-xl font-medium">
            View/Update Audit Reports <span>({category})</span>
          </h1>
        </div>

        <Steps current={currentStep} onChange={(step) => setCurrentStep(step)}>
          <Step title="FSSAI License" />
          {Array.isArray(auditItems) && auditItems.map((item, index) => (
            <Step key={index} title={item?.title || item?.label || `Step ${index + 1}`} />
          ))}
          {/* Add a final step for Suggestions when service is TPA */}
          {auditService?.toUpperCase() === "TPA" && <Step title="Suggestions" />}
        </Steps>

        {loading ? (
          <div className="flex justify-center mt-8">
            <Spin size="medium" />
          </div>
        ) : (
          <>
            <div className="flex justify-between mt-8">
              <h2 className="text-lg text-gray-600">
                {currentStep === 0 ? "FSSAI License" : (Array.isArray(auditItems) ? (currentStep <= auditItems.length ? auditItems[currentStep - 1]?.title || auditItems[currentStep - 1]?.label : "Suggestions") : "")}
              </h2>
              <div className="flex items-center gap-2">
                {currentStep > auditItems.length && auditService?.toUpperCase() === "TPA" && (
                  <Button
                    disabled={!["draft", "modified"].includes(firstSegment)}
                    onClick={() => setSuggestionsEditing((s) => !s)}
                    type={suggestionsEditing ? "default" : "primary"}
                  >
                    {suggestionsEditing ? "Cancel Edit" : "Edit Suggestions"}
                  </Button>
                )}


                <Button type="link" onClick={goBack} icon={<HomeOutlined />} size="large">
                  Home
                </Button>
              </div>
            </div>

            {currentStep === 0 ? (
              <UpdateFssaiForm />
            ) : currentStep > 0 && currentStep <= auditItems.length ? (
              renderQuestionsUI()
            ) : (
              // Suggestions view (final step)
              auditService?.toUpperCase() === "TPA" && (
                <div className="mt-6 p-4 bg-white rounded-md shadow-sm">
                  <div className="flex justify-between items-center">
                    <h3 className="mb-3 text-base font-medium">Suggestions</h3>
                    {/* small count */}
                    <Text type="secondary">{suggestions.length} suggestion(s)</Text>
                  </div>

                  {/* Read-only view */}
                  {!suggestionsEditing && (
                    <div>
                      {suggestions && suggestions.length ? (
                        suggestions.map((s, idx) => (
                          <div key={idx} className="p-3 border rounded mb-2 bg-gray-50">
                            <Text>{s}</Text>
                          </div>
                        ))
                      ) : (
                        <Text type="secondary">No suggestions added yet.</Text>
                      )}
                    </div>
                  )}

                  {/* Editable form view */}
                  {suggestionsEditing && (
                    <Form form={suggestionsForm} onFinish={handleSaveSuggestions} initialValues={{ suggestions: suggestions ? suggestions.map(s => ({ value: s })) : [] }}>
                      <Form.List name="suggestions">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, fieldKey, ...restField }) => (
                              <div key={key} className="flex items-start gap-2 mb-3">
                                <Form.Item
                                  {...restField}
                                  name={[name, "value"]}
                                  fieldKey={[fieldKey, "value"]}
                                  rules={[{ required: true, message: "Please enter suggestion" }]}
                                  style={{ flex: 1 }}
                                >
                                  <Input.TextArea rows={2} placeholder="Add suggestion or improvement..." />
                                </Form.Item>
                                <Button
                                  type="text"
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => remove(name)}
                                />
                              </div>
                            ))}

                            <Form.Item>
                              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                Add Suggestion
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>

                      <div className="flex justify-end mt-4 gap-2">
                        <Button onClick={() => { setSuggestionsEditing(false); suggestionsForm.setFieldsValue({ suggestions: suggestions.map(s => ({ value: s })) }); }}>
                          Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                          Save Suggestions
                        </Button>
                      </div>
                    </Form>
                  )}
                </div>
              )
            )}
          </>
        )}
      </div>
    </AdminDashboard>
  );
};

export default AuditReport;
